import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListSubheader from "@mui/material/ListSubheader";
import ListItemText from "@mui/material/ListItemText";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LogoutIcon from "@mui/icons-material/Logout";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import DashboardBanner from "./DashboardBanner";
import { useState } from "react";
import useWebsite from "../../services/useWebsite";
import { Button, CircularProgress, Grid } from "@mui/material";
import DashboardAbout from "./DashboardAbout";
import DashboardTestimonials from "./DashboardTestimonials";
import DashboardReports from "./DashboardReports";
import DashboardForm from "./DashboardForm";
import DashboardContact from "./DashboardContact";
import DashboardFooter from "./DashboardFooter";
import { useNavigate } from "react-router-dom";
import useAuth from "../../services/useAuth";
import {
	ALL_SECTIONS,
	INBOX_SECTIONS,
	ISection,
	NEWS_SECTIONS,
	PAGE_SECTIONS,
} from "./sections";
import DashboardViewReports from "./DasboardViewReports";
import DashboardViewContacts from "./DashboardViewContacts";
import CreateBlog from "../blogs/CreateBlog";
import BlogsList from "../blogs/BlogsList";

const drawerWidth = 240;

interface Props {
	/**
	 * Injected by the documentation to work in an iframe.
	 * You won't need it on your project.
	 */
	window?: () => Window;
}

export default function ResponsiveDrawer(props: Props) {
	const { window } = props;
	const [mobileOpen, setMobileOpen] = useState(false);
	const [currentOption, setCurrentOption] = useState("banner");
	const { website, loading, setWebsite, updateWebsite } = useWebsite();
	const { signOut } = useAuth();
	const navigate = useNavigate();

	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	};

	const current = ALL_SECTIONS.find((s) => s.key === currentOption);

	const options = [
		{
			label: "Volver al inicio",
			icon: <ArrowBackIcon />,
			action: () => navigate("/"),
		},
		{
			label: "Cerrar sesión",
			icon: <LogoutIcon />,
			action: async () => {
				await signOut();
				navigate("/");
			},
		},
	];

	const renderGroup = (title: string, items: ISection[]) => (
		<List
			dense
			subheader={
				<ListSubheader component="div" sx={{ lineHeight: "32px" }}>
					{title}
				</ListSubheader>
			}
		>
			{items.map((section) => (
				<ListItem key={section.key} disablePadding>
					<ListItemButton
						selected={currentOption === section.key}
						onClick={() => {
							setCurrentOption(section.key);
							setMobileOpen(false);
						}}
					>
						<ListItemText primary={section.label} />
					</ListItemButton>
				</ListItem>
			))}
		</List>
	);

	const drawer = (
		<div>
			<Toolbar />
			<Divider />
			{renderGroup("Secciones de la página", PAGE_SECTIONS)}
			<Divider />
			{renderGroup("Noticias", NEWS_SECTIONS)}
			<Divider />
			{renderGroup("Lo que envía la gente", INBOX_SECTIONS)}
			<Divider />
			<List dense>
				{options.map((item, index) => (
					<ListItem key={index} disablePadding onClick={item.action}>
						<ListItemButton>
							<ListItemIcon>{item.icon}</ListItemIcon>
							<ListItemText primary={item.label} />
						</ListItemButton>
					</ListItem>
				))}
			</List>
		</div>
	);

	const container =
		window !== undefined ? () => window().document.body : undefined;

	const handleGlobalChanges = (id: string, body: any, autoSave?: boolean) => {
		const updates = { ...website, [id]: body } as any;
		if (website) setWebsite(updates);
		// guarda `updates`, no `website`: el estado aún no se ha actualizado en este render
		autoSave && updateWebsite(updates);
	};

	return (
		<Box sx={{ display: "flex" }}>
			<CssBaseline />

			<AppBar
				position="fixed"
				sx={{
					width: { sm: `calc(100% - ${drawerWidth}px)` },
					ml: { sm: `${drawerWidth}px` },
				}}
			>
				<Toolbar>
					<IconButton
						color="inherit"
						aria-label="open drawer"
						edge="start"
						onClick={handleDrawerToggle}
						sx={{ mr: 2, display: { sm: "none" } }}
					>
						<MenuIcon />
					</IconButton>

					<Typography variant="h6" noWrap component="div">
						Panel de administración
					</Typography>
					<Box sx={{ flexGrow: 1 }} />

					{loading ? (
						<Box sx={{ display: "flex" }}>
							<CircularProgress
								color="inherit"
								style={{ color: "white" }}
							/>
						</Box>
					) : (
						<Button
							style={{ backgroundColor: "white" }}
							color="inherit"
							onClick={() => updateWebsite(website)}
						>
							Guardar cambios
						</Button>
					)}
				</Toolbar>
			</AppBar>
			<Box
				component="nav"
				sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
				aria-label="mailbox folders"
			>
				{/* The implementation can be swapped with js to avoid SEO duplication of links. */}
				<Drawer
					container={container}
					variant="temporary"
					open={mobileOpen}
					onClose={handleDrawerToggle}
					ModalProps={{
						keepMounted: true, // Better open performance on mobile.
					}}
					sx={{
						display: { xs: "block", sm: "none" },
						"& .MuiDrawer-paper": {
							boxSizing: "border-box",
							width: drawerWidth,
						},
					}}
				>
					{drawer}
				</Drawer>
				<Drawer
					variant="permanent"
					sx={{
						display: { xs: "none", sm: "block" },
						"& .MuiDrawer-paper": {
							boxSizing: "border-box",
							width: drawerWidth,
						},
					}}
					open
				>
					{drawer}
				</Drawer>
			</Box>
			{website ? (
				<Box
					component="main"
					sx={{
						marginTop: "20px",
						p: 3,
						width: `calc(100dvw - ${drawerWidth}px)`,
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
					}}
				>
					<Toolbar />

					{current && (
						<Box sx={{ mb: 3 }}>
							<Typography variant="h5" fontWeight={800}>
								{current.label}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{current.hint}
							</Typography>
						</Box>
					)}

					{currentOption === "banner" && (
						<DashboardBanner
							item={website.banner}
							onChange={(banner: any) =>
								handleGlobalChanges("banner", banner)
							}
						/>
					)}
					{currentOption === "about" && (
						<DashboardAbout
							item={website.about}
							onChange={(about: any, autoSave?: boolean) =>
								handleGlobalChanges("about", about, autoSave)
							}
						/>
					)}
					{currentOption === "testimonials" && (
						<DashboardTestimonials
							item={website.testimonials}
							onChange={(banner: any) =>
								handleGlobalChanges("testimonials", banner)
							}
						/>
					)}
					{currentOption === "reports" && (
						<DashboardReports
							item={website.reports}
							onChange={(banner: any) =>
								handleGlobalChanges("reports", banner)
							}
						/>
					)}
					{currentOption === "reportForm" && (
						<DashboardForm
							item={website.reportForm}
							onChange={(banner: any) =>
								handleGlobalChanges("reportForm", banner)
							}
						/>
					)}
					{currentOption === "contact" && (
						<DashboardContact
							item={website.contact}
							onChange={(banner: any) =>
								handleGlobalChanges("contact", banner)
							}
						/>
					)}
					{currentOption === "footer" && (
						<DashboardFooter
							item={website.footer}
							onChange={(banner: any) =>
								handleGlobalChanges("footer", banner)
							}
						/>
					)}
					{currentOption === "blog-new" && (
						<CreateBlog
							onFinish={() =>
								setCurrentOption("blog-list")
							}
						/>
					)}

					{currentOption === "inbox-reports" && (
						<DashboardViewReports />
					)}

					{currentOption === "inbox-contacts" && <DashboardViewContacts />}
					{currentOption === "blog-list" && (
						<BlogsList
							onEdit={() => {}}
							onReset={() => setCurrentOption("blog-new")}
						/>
					)}
				</Box>
			) : (
				<Grid
					container
					sx={{
						marginTop: "20px",
						display: "flex",
						height: "100vh",
						width: `90vw`,
						justifyContent: "center",
						alignItems: "center",
						alignContent: "center",
					}}
				>
					<CircularProgress
						color="primary"
						style={{ alignSelf: "center" }}
					/>
				</Grid>
			)}
		</Box>
	);
}
